import { getAppStore } from '../../app/AppStore';
import * as SiteUrlsSelectors from '../../app/selectors/SiteUrlsSelectors';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';

import { Module } from '../../ts/Module';
import { AddAnalyticsExtConnectionSettingsRequest } from '../../ts/ServerApi/requests/AddAnalyticsExtConnectionSettingsRequest';
import { DeleteAnalyticsExtConnectionSettingsRequest } from '../../ts/ServerApi/requests/DeleteAnalyticsExtConnectionSettingsRequest';
import { GetAnalyticsExtConnectionSettingsRequest, IGetAnalyticsExtConnectionSettingsResult } from '../../ts/ServerApi/requests/GetAnalyticsExtConnectionSettingsRequest';
import { GetAnalyticsExtSiteSettingsRequest, IGetAnalyticsExtSiteSettingsResult } from '../../ts/ServerApi/requests/GetAnalyticsExtSiteSettingsRequest';
import { GetExtsvcServerSettingsRequest, IGetExtsvcServerSettingsResult } from '../../ts/ServerApi/requests/GetExtsvcServerSettingsRequest';
import { UpdateAnalyticsExtConnectionSettingsRequest } from '../../ts/ServerApi/requests/UpdateAnalyticsExtConnectionSettingsRequest';

import { UpdateAnalyticsExtSiteSettingsRequest } from '../../ts/ServerApi/requests/UpdateAnalyticsExtSiteSettingsRequest';
import { UpdateExtsvcServerSettingsRequest } from '../../ts/ServerApi/requests/UpdateExtsvcServerSettingsRequest';
import { IExtsvcServerSettings } from '../../ts/ServerApi/types/IEditableServerSettings';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';

import { SettingsFormService } from '../directives/tbSettingsCoordinator';
import { IAnalyticsExtConnectionId, IAnalyticsExtConnectionSettings, IAnalyticsExtSiteSettings } from '../types/ISiteSettingsForSiteAdmin';
import { BaseReactSettingsCtrl, BaseReactSettingsCtrlProps, BaseReactSettingsCtrlScope } from './BaseReactSettingsCtrl';


export interface IAnalyticsExtensionsSettings {
  //enableServerSetting: IExtsvcServerSettings;
  enableExtSvcSite: boolean;
  connectionSettingsList: IAnalyticsExtConnectionSettings[];
  connectionDefault: IAnalyticsExtConnectionSettings;
}

export interface AnalyticsExtensionsSettingsCtrlProps extends BaseReactSettingsCtrlProps {
  message: string;
  isOnline: boolean;
  sendUpdateRequest: (siteSettings: IAnalyticsExtSiteSettings, connectionSettings: IAnalyticsExtConnectionSettings) => ng.IPromise<IResult>;
  sendUpdateConenctionRequest: (connectionSettings: IAnalyticsExtConnectionSettings) => ng.IPromise<IResult>;
  getConfigSettings: () => ng.IPromise<IAnalyticsExtensionsSettings>;
  sendDeleteRequest: (connectionId: IAnalyticsExtConnectionId) => ng.IPromise<IResult>;
  initialAnalyticsExtensionsSettings: IAnalyticsExtensionsSettings; //AnalyticsExtensionsSettings;
  displaySettings: boolean;
  displaySiteSettingCheckbox: boolean;
}

export class AnalyticsExtensionsSettingsCtrl extends BaseReactSettingsCtrl<AnalyticsExtensionsSettingsCtrlProps> {

  private static formName: string = 'analyticsExtSiteSettingsForm';
  private currentSetting: IAnalyticsExtensionsSettings = {
    enableExtSvcSite: undefined,
    connectionSettingsList: undefined,
    connectionDefault:undefined
  };
  private isSingleSiteOnPremAdministrator: boolean;
  private isExtensionSettingPage: boolean;
  private showAnalyticsExtSiteCheckbox: boolean;
  private isMultiSite: boolean;
  private toasterService = ToasterService.instance;

  public static $inject = [
    '$scope',
    'SettingsFormService',
    '$translate',
    '$location',
  ];

  constructor(
    private $scope: BaseReactSettingsCtrlScope<AnalyticsExtensionsSettingsCtrlProps>,
    formService: SettingsFormService,
    private $translate: ng.translate.ITranslateService,
    private $location: ng.ILocationService,
  ) {
    super($scope, formService, AnalyticsExtensionsSettingsCtrl.formName);
    const serverService = getSessionManagementServiceInstance();
    // this is a work around to get if this is single site, there is no direct way to know
    // isMultiSite means if user has access to multisite
    this.isMultiSite = serverService.isSessionMultiSite();
    const isOnline = serverService.isOnlineEnvironment(); //Using new online variable as directed by VizPortalClient
    const siteUrls = SiteUrlsSelectors.siteUrlsSelector(getAppStore().getState());
    this.isSingleSiteOnPremAdministrator = (!this.isMultiSite && !isOnline);
    this.isExtensionSettingPage = ($location.path() === siteUrls.settingsExtensionsPath()); //($location.path() === siteUrls.settingsExtensionsPath()) || !isMultiSite;

    // Get the initial data from the server
    this.getInitialSettingsInfo().then((result: IAnalyticsExtensionsSettings) => {
      $scope.props = {
        ...$scope.props,
        initialAnalyticsExtensionsSettings: result,
        // if it's singlesite or at extension site setting page then display it
        displaySettings: !this.isMultiSite || this.isExtensionSettingPage,
        displaySiteSettingCheckbox: this.showAnalyticsExtSiteCheckbox,
        isOnline: isOnline
      };
    }, reason => {
      $scope.props = {
        ...$scope.props,
        initialAnalyticsExtensionsSettings: reason,
        displaySettings: !this.isMultiSite || this.isExtensionSettingPage,
        displaySiteSettingCheckbox: this.showAnalyticsExtSiteCheckbox,
        isOnline: isOnline
      };
    });

    // Setup our one prop
    $scope.props.sendUpdateRequest = this.UpdateAnExtSiteConnectionSettings.bind(this);
    $scope.props.sendUpdateConenctionRequest = this.UpdateAnalyticsExtConnectionSettings.bind(this);
    $scope.props.getConfigSettings = this.getInitialSettingsInfo.bind(this);
    $scope.props.sendDeleteRequest = this.DeleteAnExtConnection.bind(this);
  }

  private async getInitialSettingsInfo(): Promise<IAnalyticsExtensionsSettings>  {
    if (this.isSingleSiteOnPremAdministrator){
      // when it's single site, site setting checkbox should always shows
      this.showAnalyticsExtSiteCheckbox = true;
      return this.GetAnalyticsSiteConnectionSettingsSinglesite().finally(() => this.$scope.$applyAsync());
    }else {
      let result: IGetExtsvcServerSettingsResult = await WebHttpClientWithErrorToastInstance.sendTagRequest(new GetExtsvcServerSettingsRequest());
      this.showAnalyticsExtSiteCheckbox = result.enabled;
      return this.GetAnalyticsSiteConnectionSettingsMultisite().finally(() => this.$scope.$applyAsync());
    }
  }

  public GetAnalyticsSiteConnectionSettingsMultisite(): ng.IPromise<IAnalyticsExtensionsSettings> {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtSiteSettingsRequest()).then(
      (result: IGetAnalyticsExtSiteSettingsResult) => {
        if(!result || (result as any).errors){
          this.toasterService.pop({ type: ToastType.Error, text: this.$translate.instant('errorConfigurationNotAvailable') });
          this.currentSetting.enableExtSvcSite = false;
        } else {this.currentSetting.enableExtSvcSite = result.enabled;}

        return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtConnectionSettingsRequest()).then(
          (result: IGetAnalyticsExtConnectionSettingsResult) => {
            if(!result || (result as any).errors){
              this.toasterService.pop({ type: ToastType.Error, text: this.$translate.instant('errorConfigurationNotAvailable') });
              this.currentSetting.connectionSettingsList = [];
            }else {
              this.currentSetting.connectionSettingsList = result.connectionList;
              this.currentSetting.connectionDefault = result.connectionList[0];
            }
            return this.currentSetting;
          });

      });
  }

  public GetAnalyticsSiteConnectionSettingsSinglesite(): ng.IPromise<IAnalyticsExtensionsSettings>{
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetExtsvcServerSettingsRequest()).then(
      (result: IGetExtsvcServerSettingsResult) => {
        if(!result || (result as any).errors){
          this.currentSetting.enableExtSvcSite = false;
          this.toasterService.pop({ type: ToastType.Error, text: this.$translate.instant('errorConfigurationNotAvailable') });
        } else {this.currentSetting.enableExtSvcSite = result.enabled;}

        return WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtConnectionSettingsRequest()).then(
          (result: IGetAnalyticsExtConnectionSettingsResult) => {
            if(!result || (result as any).errors){
              this.toasterService.pop({ type: ToastType.Error, text: this.$translate.instant('errorConfigurationNotAvailable') });
              this.currentSetting.connectionSettingsList = [];
            }else {
              this.currentSetting.connectionSettingsList = result.connectionList;
              this.currentSetting.connectionDefault = result.connectionList[0];
            }
            return this.currentSetting;
          });

      });
  }

  public UpdateAnalyticsExtSiteSettings(siteSetting: IAnalyticsExtSiteSettings): ng.IPromise<IResult> {
    if (this.isSingleSiteOnPremAdministrator) {
      const serverSetting: IExtsvcServerSettings = { enabled: siteSetting.enabled };

      return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateExtsvcServerSettingsRequest(serverSetting))
        .then((result: IResult) => {
          return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateAnalyticsExtSiteSettingsRequest(siteSetting));
        });
    }else{
      return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateAnalyticsExtSiteSettingsRequest(siteSetting));
    }
  }

  public UpdateAnalyticsExtConnectionSettings(connectionSettings: IAnalyticsExtConnectionSettings):
    ng.IPromise<IResult> {
    // if this is the first time setting connection setting then need to call add instead of update
    if (connectionSettings.connection_luid === undefined || connectionSettings.connection_luid === '') {
      return WebHttpClientWithErrorToastInstance.sendTagRequest(new AddAnalyticsExtConnectionSettingsRequest(connectionSettings));
    } else {
      return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateAnalyticsExtConnectionSettingsRequest(connectionSettings));
    }
  }

  public UpdateAnExtSiteConnectionSettings(
    siteSetting: IAnalyticsExtSiteSettings, connectionSettings: IAnalyticsExtConnectionSettings): ng.IPromise<IResult>{

    if (connectionSettings) {
      return this.UpdateAnalyticsExtSiteSettings(siteSetting)
        .then((result: IResult) => {
          return this.UpdateAnalyticsExtConnectionSettings(connectionSettings);
        });
    } else {
      return this.UpdateAnalyticsExtSiteSettings(siteSetting);
    }
  }

  public DeleteAnExtConnection(connectionId: IAnalyticsExtConnectionId): ng.IPromise<IResult>{
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new DeleteAnalyticsExtConnectionSettingsRequest(connectionId));
  }
}

// Remember to register the controller with Angular
Module.instance().controller('AnalyticsExtensionsSettingsCtrl', AnalyticsExtensionsSettingsCtrl);
